import { put, call, takeLatest } from 'redux-saga/effects';
import * as actionTypes from './actionTypes';
import * as actions from './actions';
import * as claimService from '../../services/claimService';
import * as types from '../../utils/constants/claimStatusTypes';
import { formatLossDateToEST } from '../../utils/common/dates/estDateTime';

function* getClaims() {
  try {
    const response = yield call(claimService.getClaimsData);
    if (response.errors) {
      yield put(actions.getClaimsFailed(response.errors));
    } else {
      yield put(actions.getClaimsSuccess(response.data.getClaims));
    }
  } catch (error) {
    yield put(actions.getClaimsFailed(error));
  }
}

function* getGWClaims() {
  try {
    const response = yield call(claimService.getGWClaims);
    if (response.errors) {
      yield put(actions.getGWClaimsFailed(response.errors));
    } else {
      yield put(actions.getGWClaimsSuccess(response.data.getGWClaims));
    }
  } catch (error) {
    yield put(actions.getGWClaimsFailed(error));
  }
}

function* createClaim(options) {
  try {
    const response = yield call(claimService.createClaim, options.lang);
    if (response.errors) {
      yield put(actions.createClaimFailed(response.errors));
    } else {
      yield put(actions.createClaimSuccess(response.data.createClaim));
    }
  } catch (error) {
    yield put(actions.createClaimFailed(error));
  }
}

function* updateClaim(options) {
  /**
   * Using session storage to save options
   * Formio Components has feature to auto save the update Claim
   * Call claimService.updateClaim only if there is change on options payload
   */
  try {
    const { claimId, status, submissionData, submissionDataGW, isUpdateToGwRequired, lang, metadata, leaveForm, lossDetailsConfirmed } = options.payload;
    const response = yield call(claimService.updateClaim, claimId, status, submissionData, submissionDataGW, isUpdateToGwRequired, lang, metadata, lossDetailsConfirmed);
    if (response.data?.data?.updateClaim) {
      if (response.data.data.updateClaim.cdsClaimNumber && !leaveForm) {
        window.sessionStorage.setItem('claimNumber', response.data.data.updateClaim.cdsClaimNumber);
      }
      const { idMapping, submittedDataGW: { scheduledItems } } = response.data.data.updateClaim;
      if (scheduledItems && !leaveForm) {
        window.sessionStorage.setItem('scheduledItems', JSON.stringify(scheduledItems));
      }

      if (idMapping && !leaveForm) {
        window.sessionStorage.setItem('idMapping', JSON.stringify(idMapping));
      }
    }
    if (response.data.errors) {
      yield put(actions.updateClaimFailed(response.data.errors));
    } else {
      if (!leaveForm) {
        window.sessionStorage.setItem('updateClaimPayload', JSON.stringify({
          type: options.type,
          payload: { ...response.data.data.updateClaim },
        }));
      }
      yield put(actions.updateClaimSuccess({ ...response.data.data.updateClaim, leaveForm }));
    }
  } catch (error) {
    yield put(actions.updateClaimFailed(error));
  }
}

function* createShellClaim(options) {
  const { draftClaimId, policyNumber, dateOfLoss, sourceSystem, preferredLanguage, vehiclePolicySystemId, dwellingPolicySystemId } = options.payload;
  try {
    const dateOfLossEST = formatLossDateToEST(dateOfLoss);
    const response = yield call(claimService.createShellClaim, draftClaimId, policyNumber, dateOfLossEST, sourceSystem, preferredLanguage, vehiclePolicySystemId, dwellingPolicySystemId);
    if (response.errors) {
      yield put(actions.createShellClaimFailed(response.errors));
    } else if (response.data.createShellClaim) {
      // eslint-disable-next-line camelcase
      const { claimId, gwClaimId, gwClaimNumber, policyAddress, claimServices, isDuplicated } = response.data.createShellClaim;
      if (gwClaimNumber) {
        window.sessionStorage.setItem('claimNumber', gwClaimNumber);
      }
      if (isDuplicated) {
        yield put(actions.createShellClaimDuplicated());
      } else {
        yield put(actions.createShellClaimSuccess(claimId, gwClaimId, gwClaimNumber, policyAddress, claimServices));
      }
    }
  } catch (error) {
    yield put(actions.createShellClaimFailed(error));
  }
}

function* submitClaim(options) {
  const draftClaimId = options.payload.claimId;
  try {
    const response = yield call(claimService.submitClaim, draftClaimId);
    if (response.errors) {
      yield put(actions.submitClaimFailed(response.errors));
    } else {
      const resp = response.data.submitClaim;
      if (resp.submitted) {
        window.sessionStorage.setItem('digitalFNOLStatus', types.CLAIM_STATUS_SUBMITTED);
        yield put(actions.submitClaimSuccess(resp));
      } else {
        yield put(actions.submitClaimFailed(resp));
      }
    }
  } catch (error) {
    yield put(actions.submitClaimFailed(error));
  }
}

export default function* saga() {
  yield takeLatest(actionTypes.GET_CLAIMS, getClaims);
  yield takeLatest(actionTypes.GET_GW_CLAIMS, getGWClaims);
  yield takeLatest(actionTypes.CREATE_CLAIM, createClaim);
  yield takeLatest(actionTypes.UPDATE_CLAIM, updateClaim);
  yield takeLatest(actionTypes.CREATE_SHELL_CLAIM, createShellClaim);
  yield takeLatest(actionTypes.SUBMIT_CLAIM, submitClaim);
}
